import { useState, useEffect, useRef } from 'react';
import { styled } from '@mui/material/styles';
import { Box, IconButton, Paper, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useSnackbar } from 'notistack';
import Collapse from '@mui/material/Collapse';
import Fade from '@mui/material/Fade';
import { keyframes } from '@mui/material/styles';

import Sidebar from 'src/icons/Sidebar';
import Remove from 'src/icons/Remove';
import TgMonitoringIcon from 'src/icons/TgMonitoringIcon';
import useProjectsStore from 'src/stores/projects';
import useKeywordStore from 'src/stores/keywords';
import useCollectionsStore from 'src/stores/collections';
import KeywordsService from 'src/services/KeywordsService';
import useFilterStore from 'src/stores/filter';
import { ROUTES_URL } from 'src/constants/routes';
import LeadStreamsService from 'src/services/LeadStreamsService';

import LoadingWrapper from 'src/components/Loading/LoadingWrapper';

import useUserStore from 'src/stores/user';

import AIFilter from 'src/components/AIFilter';

import { getFilters } from '../utils';
import FilterItem from '../Components/FilterItem';

// Анимация пульсации - расслабленная с большой шириной
const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(127,48,228,0.1), 0 0 12px 1px #FA00FF11; }
  70% { box-shadow: 0 0 0 20px rgba(127,48,228,0); }
  100% { box-shadow: 0 0 0 0 rgba(127,48,228,0.1), 0 0 12px 1px #FA00FF11; }
`;

const dotsAnimation = keyframes`
  0%, 20% { opacity: 0; }
  40% { opacity: 1; }
  60%, 100% { opacity: 0; }
`;

const ONBOARDING_MESSAGES = {
    email_confirm: 'Подтвердите почту для продолжения настройки сервиса',
    description: 'Расскажите о своём бизнесе - это поможет настроить поиск лидов',
    choose_plan: 'Выберите тариф и запустите круглосуточный поиск лидов',
    choose_collections: 'Подборки - это готовые наборы чатов. Выберите подходящие подборки для вашего бизнеса',
    add_chats: 'Добавьте чаты если хотите мониторить конкретные чаты',
    add_keywords: 'Добавьте ключевые слова - по ним будут искаться лиды',
    completed: 'Настройка завершена! Мониторинг активен',
};

export default function Filters() {
    const { enqueueSnackbar } = useSnackbar();
    const isOnboardingCompleted = useUserStore(({ isOnboardingCompleted }) => isOnboardingCompleted);
    const { activeProject } = useProjectsStore(({ activeProject }) => ({ activeProject }));
    const keywords = useKeywordStore(({ keywords }) => keywords);
    const [open, setOpen] = useState(isOnboardingCompleted);
    const location = useLocation();

    const { isLoading: isLoadingKeywords } = useQuery({
        queryKey: KeywordsService.queryKey,
        queryFn: useKeywordStore.getState().fetchKeywords,
        onError: () => {
            enqueueSnackbar('Ошибка получения ключевых слов', { variant: 'error' });
        },
        enabled: keywords.length < 1,
    });

    const { data: streams = [], isLoading: isLoadingStreams } = useQuery({
        queryKey: LeadStreamsService.queryKey,
        queryFn: LeadStreamsService.getStreams,
        onError: () => {
            // Не показываем ошибку, если streams не загрузились - это не критично
        },
        select: (data) => data.filter((stream) => stream.is_active), // Фильтруем только активные потоки
    });

    const { privateCollection: privateChats } = useCollectionsStore(({ privateCollection }) => ({ privateCollection }));
    const current_subscription = useUserStore((state) => state.current_subscription);
    const monitoringActive = !!current_subscription;

    const handleChangeOpen = () => {
        setOpen((old) => !old);
    };

    const handleClear = () => {
        useFilterStore.getState().clearFilter();
    };

    const { projects } = useProjectsStore(({ projects }) => ({ projects }));

    const items = getFilters({
        keywords,
        chats: privateChats?.chats || [],
        bundles: activeProject?.bundles.filter((bundle) => bundle.is_public) || [],
        projects: projects || [],
        streams: streams || [],
    });

    const isFeedPage = location.pathname === ROUTES_URL.FEED;

    return (
        <LoadingWrapper isLoading={isLoadingKeywords || isLoadingStreams}>
            <Wrapper
                elevation={0}
                square={true}
            >
                <Container elevation={0}>
                    {/* AI-блок - показывается везде */}
                    <Box sx={{ background: 'none !important', p: 0, padding: 0, m: 0, borderRadius: 2 }}>
                        <AIMonitoringBlock monitoringActive={monitoringActive} />
                    </Box>

                    {/* Фильтры - только на странице feed */}
                    {isFeedPage && (
                        <>
                            <Box
                                display="flex"
                                width="100%"
                                alignItems="center"
                                justifyContent="space-between"
                                mb={2}
                            >
                                <Typography
                                    fontWeight="600"
                                    variant="h4"
                                >
                                    Фильтры
                                </Typography>
                                <Box
                                    ml="auto"
                                    title="Очистить"
                                >
                                    <IconButton onClick={handleClear}>
                                        <Remove />
                                    </IconButton>
                                </Box>
                                <IconButton onClick={handleChangeOpen}>
                                    <Sidebar />
                                </IconButton>
                            </Box>
                            <Collapse
                                in={open}
                                timeout="auto"
                                unmountOnExit
                            >
                                <AIFilter />
                                {items.map((item) => {
                                    if (item.options[0].items.length === 0) return null;
                                    return (
                                        <FilterItem
                                            key={item.title}
                                            {...item}
                                        />
                                    );
                                })}
                            </Collapse>
                        </>
                    )}
                </Container>
            </Wrapper>
        </LoadingWrapper>
    );
}

const Wrapper = styled(Paper)(() => ({
    width: '100%',
    height: '100%',
    padding: '1rem 1rem 1rem 0',
}));

const Container = styled(Paper)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    backgroundColor: theme.palette.background.default,
    overscrollBehavior: 'contain',
    padding: theme.spacing(2),
    borderRadius: 24,
    position: 'relative',
    overflowY: 'auto',
    overflowX: 'hidden',
    maxHeight: 'calc(100vh - 120px)', // Ограничиваем высоту для скролла
    '&::-webkit-scrollbar': {
        width: '6px',
    },
    '&::-webkit-scrollbar-track': {
        background: '#f1f1f1',
        borderRadius: '3px',
    },
    '&::-webkit-scrollbar-thumb': {
        background: '#c1c1c1',
        borderRadius: '3px',
    },
    '&::-webkit-scrollbar-thumb:hover': {
        background: '#a8a8a8',
    },
}));
// AI Monitoring Block с умными подсказками - независимый от пересоздания
function AIMonitoringBlock({ monitoringActive }: { monitoringActive: boolean }) {
    const [logIndex, setLogIndex] = useState(() => Math.floor(Math.random() * 4)); // Начинаем с рандомного сообщения
    const [fade, setFade] = useState(true);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const isInitializedRef = useRef(false);

    const isOnboardingCompleted = useUserStore(({ isOnboardingCompleted }) => isOnboardingCompleted);
    const onboardingStep = useUserStore(({ onboardingStep }) => onboardingStep);
    const current_subscription = useUserStore((state) => state.current_subscription);
    const keywords = useKeywordStore(({ keywords }) => keywords);
    const { privateCollection } = useCollectionsStore(({ privateCollection }) => ({ privateCollection }));
    const { activeProject } = useProjectsStore(({ activeProject }) => ({ activeProject }));

    // Определяем статус готовности
    const hasSubscription = !!current_subscription;
    const hasKeywords = keywords.length > 0;
    const hasChats = privateCollection?.chats && privateCollection.chats.length > 0;
    const hasBundles = activeProject?.bundles && activeProject.bundles.filter((b) => b.is_public).length > 0;
    const hasChatsOrBundles = hasChats || hasBundles;
    const hasProject = activeProject && activeProject.id > 0; // Проверяем что проект создан
    const isReady = hasSubscription && hasKeywords && hasChatsOrBundles && hasProject;

    // Получаем текущий шаг онбординга
    const getCurrentOnboardingStep = () => {
        const steps = [
            'email_confirm',
            'description',
            'choose_collections',
            'add_chats',
            'add_keywords',
            'choose_plan',
            'completed',
        ];
        return steps[onboardingStep - 1] || 'completed';
    };

    // Генерируем сообщения для активного мониторинга
    const getActiveMessages = () => {
        const messages = ['Фильтрую спам сообщения', 'Обрабатываю новые сообщения'];

        if (hasChats) {
            const chats = privateCollection?.chats || [];
            if (chats.length > 0) {
                const firstChat = chats[0];
                const chatName = firstChat.name || firstChat.telegram_link || 'чат';
                if (chats.length === 1) {
                    messages.push(`Мониторю чат "${chatName}" на лиды`);
                } else if (chats.length === 2) {
                    const secondChat = chats[1];
                    const secondChatName = secondChat.name || secondChat.telegram_link || 'чат';
                    messages.push(`Проверяю "${chatName}" и "${secondChatName}" на лиды`);
                } else {
                    messages.push(
                        `Мониторю ${chatName} и еще ${chats.length - 1} чат${chats.length - 1 > 1 ? 'ов' : ''} на лиды`
                    );
                }
            }
        }

        if (hasBundles) {
            const bundles = activeProject?.bundles?.filter((b) => b.is_public) || [];
            if (bundles.length > 0) {
                // Показываем каждую подборку отдельно
                bundles.forEach((bundle) => {
                    const bundleName = bundle.name || 'подборка';
                    messages.push(`Мониторю подборку "${bundleName}" на лиды`);
                });
            }
        }

        if (hasKeywords) {
            const keywordList = keywords.map((k) => k.word);
            if (keywordList.length === 1) {
                messages.push(`Ищу лиды по ключевому слову "${keywordList[0]}"`);
            } else {
                // Показываем каждое ключевое слово отдельно
                keywordList.forEach((keyword) => {
                    messages.push(`Ищу лиды по слову "${keyword}"`);
                });
            }
        }

        messages.push('Обновляю статистику найденных лидов');
        return messages;
    };

    // Получаем подсказку в зависимости от статуса
    const getHintMessage = () => {
        if (!isOnboardingCompleted) {
            const currentStep = getCurrentOnboardingStep();
            let msg = ONBOARDING_MESSAGES[currentStep as keyof typeof ONBOARDING_MESSAGES] || 'Завершите настройку';
            if (msg === 'Запуск мониторинга') msg = 'Идёт поиск лидов';
            return msg;
        }

        if (!hasSubscription) {
            return 'Выберите тариф и начнется мониторинг лидов в реальном времени';
        }

        if (!hasKeywords) {
            return 'Мониторинг остановлен. Добавьте ключевые слова - по ним будут искаться лиды';
        }

        if (!hasChatsOrBundles) {
            return 'Выберите где искать лиды: добавьте чаты или выберите готовые подборки';
        }

        if (!hasProject) {
            return 'Создайте проект для запуска мониторинга';
        }

        return null; // Всё готово
    };

    useEffect(() => {
        // Инициализируем только один раз
        if (!isInitializedRef.current) {
            isInitializedRef.current = true;
        }

        if (isReady) {
            const activeMessages = getActiveMessages();
            intervalRef.current = setInterval(() => {
                setFade(false);
                setTimeout(() => {
                    // Рандомный выбор следующего сообщения
                    const randomIndex = Math.floor(Math.random() * activeMessages.length);
                    setLogIndex(randomIndex);
                    setFade(true);
                }, 350);
            }, 4000);
        } else {
            const activeMessages = getActiveMessages();
            const randomIndex = Math.floor(Math.random() * activeMessages.length);
            setLogIndex(randomIndex);
        }
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [isReady, hasChats, hasBundles, hasKeywords]);

    const hintMessage = getHintMessage();
    const activeMessages = getActiveMessages();
    const isActive = isReady;

    return (
        <Box
            mb={1}
            p={1.2}
            minHeight={101}
            sx={{
                border: '1px solid #E3ECF3',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                position: 'relative',
                overflow: 'hidden',
                minHeight: 85,
                maxHeight: 117,
            }}
        >
            <Box
                sx={{
                    background: isActive ? 'linear-gradient(135deg, #FA00FF33 0%, #FF1F0033 100%)' : 'none',
                    borderRadius: '50%',
                    p: 0.7,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 1,
                    animation: isActive ? `${pulse} 5s infinite` : 'none',
                }}
            >
                <TgMonitoringIcon />
            </Box>
            <Box
                flex={1}
                minWidth={0}
            >
                <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{
                        fontSize: 14,
                        wordBreak: 'break-word',
                        color: 'primary.main',
                    }}
                >
                    {isActive ? (
                        <Fade
                            in={fade}
                            timeout={{ enter: 350, exit: 350 }}
                        >
                            <span>
                                {activeMessages[logIndex]}
                                <span style={{ display: 'inline-block', marginLeft: 4 }}>
                                    <span
                                        style={{
                                            animation: `${dotsAnimation} 1.4s infinite`,
                                            animationDelay: '0s',
                                            display: 'inline-block',
                                            fontSize: '14px',
                                            color: 'inherit',
                                        }}
                                    >
                                        .
                                    </span>
                                    <span
                                        style={{
                                            animation: `${dotsAnimation} 1.4s infinite`,
                                            animationDelay: '0.2s',
                                            display: 'inline-block',
                                            fontSize: '14px',
                                            color: 'inherit',
                                        }}
                                    >
                                        .
                                    </span>
                                    <span
                                        style={{
                                            animation: `${dotsAnimation} 1.4s infinite`,
                                            animationDelay: '0.4s',
                                            display: 'inline-block',
                                            fontSize: '14px',
                                            color: 'inherit',
                                        }}
                                    >
                                        .
                                    </span>
                                </span>
                            </span>
                        </Fade>
                    ) : (
                        <span>{hintMessage || 'Мониторинг неактивен'}</span>
                    )}
                </Typography>
            </Box>
        </Box>
    );
}
